import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { ClipType } from '@clipr/lib';

import { MomentModel, TrackModel } from '../../entities';
import { RedactionMomentTypes } from '../../entities/player';
import { notifyError, notifyLoading, notifySuccess } from '../../services/notifications';
import { BindingProps, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { PlayerState } from '../player';
import {
  moveRegion,
  normalizeRegion,
  parseRedaction,
  RedactionRegion,
  RedactionResizeHandle,
  resizeRegion,
  selectionOffsetsToTimeRange,
  serializeRedaction,
  StoredTranscriptRedaction,
  StoredVisualRedaction,
  VisualRedactionEffect,
  VisualRedactionShape,
} from './trainerRedactionUtils';

const VISUAL_TRACK_NAME = 'redaction.visual.v1';
const AUDIO_TRACK_NAME = 'redaction.audio.v1';

type CanvasPoint = { x: number; y: number };

type RegionTransform = {
  kind: 'move' | 'resize';
  point: CanvasPoint;
  region: RedactionRegion;
  handle?: RedactionResizeHandle;
};

type Props = BindingProps<{
  jobId: string;
  player: PlayerState;
}>;

export type TranscriptSelection = {
  momentId: string;
  startOffset: number;
  endOffset: number;
  startTime: number;
  endTime: number;
  text: string;
};

export type TranscriptRedactionRange = {
  endOffset: number;
  moment: MomentModel;
  startOffset: number;
};

export class TrainerRedactionState extends StoreNode {
  readonly nodeType = 'TrainerRedaction';

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'handleActiveAudioRedaction'>(this, {
      player: computed,
      jobId: computed,
      job: computed,
      canEdit: computed,
      visualMoments: computed,
      audioMoments: computed,
      activeVisualRedactions: computed,
      activeAudioRedaction: computed,
      editingVisualMoment: computed,
      draftDurationSeconds: computed,
      isVisualMode: observable,
      isDrawing: observable,
      isSaving: observable,
      draftRegion: observable,
      draftStartTime: observable,
      draftEndTime: observable,
      editingMomentId: observable,
      visualEffect: observable,
      visualShape: observable,
      autoTrack: observable,
      durationSeconds: observable,
      transcriptSelection: observable,
      toggleVisualMode: action,
      exitVisualMode: action,
      beginDrawing: action,
      updateDrawing: action,
      finishDrawing: action,
      editVisualRedaction: action,
      beginRegionMove: action,
      beginRegionResize: action,
      updateRegionTransform: action,
      finishRegionTransform: action,
      cancelDraft: action,
      setVisualEffect: action,
      setVisualShape: action,
      setAutoTrack: action,
      setDurationSeconds: action,
      setDraftStartTime: action,
      setDraftEndTime: action,
      prepareFollowUpAtPlayhead: action,
      visualMomentType: computed,
      setTranscriptSelection: action,
      clearTranscriptSelection: action,
      reset: action,
      handleActiveAudioRedaction: action,
    });

    reaction(
      () => this.activeAudioRedaction?.id ?? null,
      activeMomentId => this.handleActiveAudioRedaction(activeMomentId),
    );
  }

  get player(): PlayerState {
    return this.resolvedProps.player;
  }

  get jobId(): string {
    return this.resolvedProps.jobId;
  }

  get job() {
    return this.store.maybeGetJob(this.jobId);
  }

  // Redactions are trainer moments; the backend only lets Trainers write jobs in their libraries.
  get canEdit(): boolean {
    return !!this.job?.hasPermission('EditTrainerMoment');
  }

  get visualMoments(): MomentModel[] {
    return this.job?.moments.filter(moment => {
      const stored = parseRedaction(moment.description);
      return stored?.kind === 'visual';
    }) ?? [];
  }

  get audioMoments(): MomentModel[] {
    return this.job?.moments.filter(moment => moment.momentType === RedactionMomentTypes.AudioMuted) ?? [];
  }

  get activeVisualRedactions() {
    const time = this.player.currentTime;
    return this.visualMoments.flatMap(moment => {
      const stored = parseRedaction(moment.description);
      return stored?.kind === 'visual' && time >= moment.startTime && time <= moment.endTime
        ? [{ moment, stored }]
        : [];
    });
  }

  get activeAudioRedaction(): MomentModel | null {
    const time = this.player.currentTime;
    return this.audioMoments.find(moment => time >= moment.startTime && time <= moment.endTime) ?? null;
  }

  get editingVisualMoment(): MomentModel | null {
    return this.editingMomentId ? this.job?.getMoment(this.editingMomentId) ?? null : null;
  }

  get draftDurationSeconds(): number {
    return Math.max(0, this.draftEndTime - this.draftStartTime);
  }

  isVisualMode = false;
  isDrawing = false;
  isSaving = false;
  draftRegion: RedactionRegion | null = null;
  draftStartTime = 0;
  draftEndTime = 0;
  editingMomentId: string | null = null;
  visualEffect: VisualRedactionEffect = 'Blur';
  visualShape: VisualRedactionShape = 'Rectangle';
  autoTrack = false;
  durationSeconds = 5;
  transcriptSelection: TranscriptSelection | null = null;

  private drawStart: CanvasPoint | null = null;
  private regionTransform: RegionTransform | null = null;
  private mutedByRedaction = false;

  toggleVisualMode = () => {
    if (!this.isVisualMode && !this.canEdit)
      return;
    this.isVisualMode = !this.isVisualMode;
    this.cancelDraft();
    this.transcriptSelection = null;

    if (this.isVisualMode)
      this.player.invoke('enterPause');
    else
      this.player.invoke('exitPause');
  };

  exitVisualMode = () => {
    if (!this.isVisualMode)
      return;

    this.isVisualMode = false;
    this.cancelDraft();
    this.player.invoke('exitPause');
  };

  beginDrawing(point: CanvasPoint) {
    if (!this.isVisualMode || this.isSaving)
      return;

    this.editingMomentId = null;
    this.regionTransform = null;
    this.drawStart = point;
    this.isDrawing = true;
    this.draftRegion = normalizeRegion(point, point);
  }

  updateDrawing(point: CanvasPoint) {
    if (!this.isDrawing || !this.drawStart)
      return;

    this.draftRegion = normalizeRegion(this.drawStart, point);
  }

  finishDrawing(point: CanvasPoint) {
    if (!this.isDrawing || !this.drawStart)
      return;

    const region = normalizeRegion(this.drawStart, point);
    this.isDrawing = false;
    this.drawStart = null;
    this.draftRegion = region.width >= 0.02 && region.height >= 0.02 ? region : null;

    if (this.draftRegion) {
      const mediaEnd = this.player.duration || this.player.currentTime + this.durationSeconds;
      const startTime = Math.min(this.player.currentTime, Math.max(mediaEnd - 0.5, 0));
      this.setDraftTiming(startTime, Math.min(mediaEnd, startTime + this.durationSeconds));
    }
  }

  editVisualRedaction = (moment: MomentModel, seekToMoment = true) => {
    const stored = parseRedaction(moment.description);
    if (!this.canEdit || stored?.kind !== 'visual')
      return;

    if (!this.isVisualMode) {
      this.isVisualMode = true;
      this.player.invoke('enterPause');
    }

    this.isDrawing = false;
    this.drawStart = null;
    this.regionTransform = null;
    this.editingMomentId = moment.id;
    this.draftRegion = { ...stored.region };
    this.visualEffect = stored.effect;
    this.visualShape = stored.shape;
    this.autoTrack = stored.autoTrack;
    this.setDraftTiming(moment.startTime, moment.endTime);

    if (seekToMoment)
      this.player.invoke('seek', { time: Math.min(moment.startTime + 0.01, moment.endTime) });
  };

  beginRegionMove(point: CanvasPoint) {
    if (!this.draftRegion || this.isSaving)
      return;

    this.regionTransform = {
      kind: 'move',
      point,
      region: { ...this.draftRegion },
    };
    this.isDrawing = true;
  }

  beginRegionResize(point: CanvasPoint, handle: RedactionResizeHandle) {
    if (!this.draftRegion || this.isSaving)
      return;

    this.regionTransform = {
      kind: 'resize',
      point,
      region: { ...this.draftRegion },
      handle,
    };
    this.isDrawing = true;
  }

  updateRegionTransform(point: CanvasPoint) {
    const transform = this.regionTransform;
    if (!transform)
      return;

    if (transform.kind === 'move') {
      this.draftRegion = moveRegion(
        transform.region,
        point.x - transform.point.x,
        point.y - transform.point.y,
      );
      return;
    }

    this.draftRegion = resizeRegion(transform.region, point, transform.handle!);
  }

  finishRegionTransform() {
    this.regionTransform = null;
    this.isDrawing = false;
  }

  cancelDraft = () => {
    this.isDrawing = false;
    this.drawStart = null;
    this.regionTransform = null;
    this.draftRegion = null;
    this.draftStartTime = 0;
    this.draftEndTime = 0;
    this.editingMomentId = null;
  };

  setVisualEffect(effect: VisualRedactionEffect) {
    this.visualEffect = effect;
  }

  setVisualShape(shape: VisualRedactionShape) {
    this.visualShape = shape;
  }

  setAutoTrack(value: boolean) {
    this.autoTrack = value;
  }

  setDurationSeconds(value: number) {
    if (!Number.isFinite(value))
      return;

    this.durationSeconds = Math.max(0.1, value);
    if (this.draftRegion) {
      const mediaEnd = this.player.duration || this.draftStartTime + this.durationSeconds;
      this.draftEndTime = Math.min(mediaEnd, this.draftStartTime + this.durationSeconds);
      this.durationSeconds = this.draftDurationSeconds;
    }
  }

  setDraftStartTime(value: number) {
    if (!Number.isFinite(value))
      return;

    const mediaEnd = this.player.duration || Math.max(this.draftEndTime, value + 0.5);
    this.draftStartTime = Math.max(0, Math.min(value, mediaEnd - 0.1));
    this.draftEndTime = Math.min(mediaEnd, Math.max(this.draftEndTime, this.draftStartTime + 0.1));
    this.durationSeconds = this.draftDurationSeconds;
  }

  setDraftEndTime(value: number) {
    if (!Number.isFinite(value))
      return;

    const mediaEnd = this.player.duration || Math.max(value, this.draftStartTime + 0.1);
    this.draftEndTime = Math.min(mediaEnd, Math.max(value, this.draftStartTime + 0.1));
    this.durationSeconds = this.draftDurationSeconds;
  }

  async saveVisualRedaction() {
    const { draftRegion, editingVisualMoment, job } = this;
    if (!draftRegion || !job || this.isSaving || this.draftEndTime <= this.draftStartTime)
      return;

    this.isSaving = true;
    notifyLoading(this, 'Saving visual redaction.');

    const stored: StoredVisualRedaction = {
      version: 1,
      kind: 'visual',
      effect: this.visualEffect,
      shape: this.visualShape,
      region: draftRegion,
      autoTrack: this.autoTrack,
    };

    let err: Error | null = null;
    if (editingVisualMoment) {
      [, err] = await job.apiUpdateMoment({
        jobId: job.id,
        momentId: editingVisualMoment.id,
        clipType: editingVisualMoment.clipType,
        momentType: this.visualMomentType,
        name: `${this.visualEffect} redaction`,
        startTime: this.draftStartTime,
        endTime: this.draftEndTime,
        description: serializeRedaction(stored),
      });
    } else {
      const track = await this.ensureTrack(VISUAL_TRACK_NAME);
      if (!track) {
        runInAction(() => this.isSaving = false);
        return;
      }

      [, err] = await job.apiAddMoment({
        jobId: job.id,
        trackId: track.id,
        clipType: ClipType.Moment,
        momentType: this.visualMomentType,
        name: `${this.visualEffect} redaction`,
        startTime: this.draftStartTime,
        endTime: this.draftEndTime,
        source: 'Trainer',
        description: serializeRedaction(stored),
        visibleToConsumer: true,
      });
    }

    runInAction(() => {
      this.isSaving = false;
      if (err) {
        notifyError(this, 'Could not save the visual redaction.');
        return;
      }

      this.cancelDraft();
      notifySuccess(this, editingVisualMoment ? 'Visual redaction updated.' : 'Visual redaction added.');
    });
  }

  async deleteEditingVisualRedaction() {
    const moment = this.editingVisualMoment;
    if (!moment || this.isSaving)
      return;

    this.isSaving = true;
    const removed = await this.undoRedaction(moment);
    runInAction(() => {
      this.isSaving = false;
      if (removed)
        this.cancelDraft();
    });
  }

  prepareFollowUpAtPlayhead = () => {
    if (!this.editingVisualMoment || !this.draftRegion)
      return;

    const mediaEnd = this.player.duration || this.player.currentTime + this.durationSeconds;
    const startTime = Math.min(this.player.currentTime, Math.max(mediaEnd - 0.1, 0));
    this.editingMomentId = null;
    this.setDraftTiming(startTime, Math.min(mediaEnd, startTime + this.durationSeconds));
  };

  get visualMomentType() {
    switch (this.visualEffect) {
      case 'Blackout': return RedactionMomentTypes.BlankScreen;
      case 'Pixelate': return RedactionMomentTypes.ScreenBlur;
      default: return RedactionMomentTypes.FaceBlur;
    }
  }

  setTranscriptSelection(moment: MomentModel, startOffset: number, endOffset: number, text: string) {
    if (!this.canEdit || !moment.description || !text.trim()) {
      this.transcriptSelection = null;
      return;
    }

    const leadingWhitespace = text.length - text.trimStart().length;
    const trailingWhitespace = text.length - text.trimEnd().length;
    const normalizedStart = startOffset + leadingWhitespace;
    const normalizedEnd = Math.max(normalizedStart, endOffset - trailingWhitespace);
    const timeRange = selectionOffsetsToTimeRange(
      moment.startTime,
      moment.endTime,
      moment.description.length,
      normalizedStart,
      normalizedEnd,
    );

    this.transcriptSelection = {
      momentId: moment.id,
      startOffset: normalizedStart,
      endOffset: normalizedEnd,
      startTime: timeRange.startTime,
      endTime: Math.min(moment.endTime, Math.max(timeRange.endTime, timeRange.startTime + 0.1)),
      text: text.trim(),
    };
  }

  clearTranscriptSelection = () => {
    this.transcriptSelection = null;
    window.getSelection()?.removeAllRanges();
  };

  async redactTranscriptSelection() {
    const selection = this.transcriptSelection;
    const transcriptMoment = selection && this.job?.getMoment(selection.momentId);
    if (!selection || !transcriptMoment)
      return;

    const stored: StoredTranscriptRedaction = {
      version: 1,
      kind: 'transcript',
      transcriptMomentId: transcriptMoment.id,
      transcriptTrackId: transcriptMoment.trackId,
      startOffset: selection.startOffset,
      endOffset: selection.endOffset,
      text: selection.text,
    };

    await this.createAudioRedaction(selection.startTime, selection.endTime, stored, 'Transcript redaction');
  }

  async redactSpeakerTrack() {
    const selection = this.transcriptSelection;
    const transcriptMoment = selection && this.job?.getMoment(selection.momentId);
    const track = transcriptMoment && this.job?.getTrack(transcriptMoment.trackId);
    if (!track || !this.job)
      return;

    const moments = track.moments;
    if (moments.length === 0)
      return;

    const stored: StoredTranscriptRedaction = {
      version: 1,
      kind: 'transcript',
      transcriptTrackId: track.id,
      entireSpeaker: true,
    };

    const created = await this.createAudioRedaction(
      Math.min(...moments.map(moment => moment.startTime)),
      Math.max(...moments.map(moment => moment.endTime)),
      stored,
      `${track.displayName} redaction`,
      false,
    );

    if (!created)
      return;

    const [, err] = await this.job.apiUpdateTrack({
      args: {
        jobId: this.job.id,
        trackId: track.id,
        visibleToConsumer: false,
      },
    });

    runInAction(() => {
      if (err)
        notifyError(this, 'The audio was muted, but the speaker transcript could not be hidden.');
      else
        notifySuccess(this, 'Speaker transcript hidden and audio muted.');
      this.clearTranscriptSelection();
    });
  }

  getTranscriptRedactions(moment: MomentModel): TranscriptRedactionRange[] {
    const textLength = moment.description?.length ?? 0;

    return this.audioMoments.flatMap(audioMoment => {
      const stored = parseRedaction(audioMoment.description);
      if (stored?.kind === 'transcript') {
        if (stored.entireSpeaker && stored.transcriptTrackId === moment.trackId)
          return [{ moment: audioMoment, startOffset: 0, endOffset: textLength }];

        if (stored.transcriptMomentId === moment.id)
          return [{
            moment: audioMoment,
            startOffset: Math.max(0, stored.startOffset ?? 0),
            endOffset: Math.min(textLength, stored.endOffset ?? textLength),
          }];

        return [];
      }

      const overlaps = audioMoment.startTime < moment.endTime && audioMoment.endTime > moment.startTime;
      return overlaps ? [{ moment: audioMoment, startOffset: 0, endOffset: textLength }] : [];
    }).sort((a, b) => a.startOffset - b.startOffset);
  }

  async undoRedaction(moment: MomentModel) {
    const job = this.job;
    if (!job || !this.canEdit)
      return false;

    const stored = parseRedaction(moment.description);
    const [, err] = await job.apiDeleteMoment({ jobId: job.id, momentId: moment.id });
    if (err) {
      notifyError(this, 'Could not undo the redaction.');
      return false;
    }

    if (stored?.kind === 'transcript' && stored.entireSpeaker && stored.transcriptTrackId) {
      await job.apiUpdateTrack({
        args: {
          jobId: job.id,
          trackId: stored.transcriptTrackId,
          visibleToConsumer: true,
        },
      });
    }

    notifySuccess(this, 'Redaction removed.');
    return true;
  }

  reset() {
    this.isVisualMode = false;
    this.cancelDraft();
    this.transcriptSelection = null;
    if (this.mutedByRedaction) {
      this.mutedByRedaction = false;
      this.player.invoke('unmute');
    }
  }

  private async createAudioRedaction(
    startTime: number,
    endTime: number,
    stored: StoredTranscriptRedaction,
    name: string,
    showSuccess = true,
  ) {
    const job = this.job;
    if (!job || this.isSaving)
      return false;

    this.isSaving = true;
    notifyLoading(this, 'Saving audio redaction.');
    const track = await this.ensureTrack(AUDIO_TRACK_NAME, RedactionMomentTypes.AudioMuted);
    if (!track) {
      runInAction(() => this.isSaving = false);
      return false;
    }

    const [, err] = await job.apiAddMoment({
      jobId: job.id,
      trackId: track.id,
      clipType: ClipType.Moment,
      momentType: RedactionMomentTypes.AudioMuted,
      name,
      startTime,
      endTime,
      source: 'Trainer',
      description: serializeRedaction(stored),
      visibleToConsumer: true,
    });

    runInAction(() => {
      this.isSaving = false;
      if (err)
        notifyError(this, 'Could not save the audio redaction.');
      else if (showSuccess)
        notifySuccess(this, 'Selection redacted and muted.');

      if (!err)
        this.clearTranscriptSelection();
    });

    return !err;
  }

  private async ensureTrack(name: string, momentType?: string): Promise<TrackModel | null> {
    const job = this.job;
    if (!job)
      return null;

    const existing = job.tracks.find(track => track.name === name);
    if (existing)
      return existing;

    const [track, err] = await job.apiAddTrack({
      args: {
        jobId: job.id,
        name,
        type: ClipType.Moment,
        ...(momentType ? { momentType } : {}),
        visibleToConsumer: true,
      },
    });

    if (err || !track) {
      notifyError(this, `Could not create ${name}.`);
      return null;
    }

    return track;
  }

  private setDraftTiming(startTime: number, endTime: number) {
    this.draftStartTime = startTime;
    this.draftEndTime = Math.max(startTime + 0.1, endTime);
    this.durationSeconds = this.draftDurationSeconds;
  }

  private handleActiveAudioRedaction(activeMomentId: string | null) {
    if (activeMomentId && !this.player.isMuted) {
      this.mutedByRedaction = true;
      this.player.invoke('mute');
      return;
    }

    if (!activeMomentId && this.mutedByRedaction) {
      this.mutedByRedaction = false;
      this.player.invoke('unmute');
    }
  }
}
